/* ═══════════════════════════════════════════════════════
   STRUCTURAL / SEISMIC REFERENCE DATA
   Beam spans, anchor bolts, seismic bracing, loads
   ═══════════════════════════════════════════════════════ */

export const fmt = (n: number, d = 2) =>
  isNaN(n) || !isFinite(n) ? "—" : n.toFixed(d).replace(/\.?0+$/, "") || "0";

// ── Wood Beam Span Tables (floor joists, headers) ──
export const floorJoistHeaders = ["Size", "Species", "Grade", "12\" OC", "16\" OC", "24\" OC"];
export const floorJoistRows: (string | number)[][] = [
  // Douglas Fir-Larch #2, 40 PSF live + 10 PSF dead
  ["2×6", "Doug Fir-Larch", "#2", "10'-3\"", "9'-4\"", "7'-8\""],
  ["2×8", "Doug Fir-Larch", "#2", "13'-6\"", "12'-3\"", "10'-2\""],
  ["2×10", "Doug Fir-Larch", "#2", "17'-3\"", "15'-8\"", "12'-11\""],
  ["2×12", "Doug Fir-Larch", "#2", "20'-11\"", "19'-0\"", "15'-8\""],
  // Southern Pine #2
  ["2×6", "Southern Pine", "#2", "10'-0\"", "9'-1\"", "7'-6\""],
  ["2×8", "Southern Pine", "#2", "13'-2\"", "12'-0\"", "9'-10\""],
  ["2×10", "Southern Pine", "#2", "16'-10\"", "15'-3\"", "12'-7\""],
  ["2×12", "Southern Pine", "#2", "20'-5\"", "18'-6\"", "15'-3\""],
  // Spruce-Pine-Fir #2
  ["2×6", "SPF", "#2", "9'-9\"", "8'-10\"", "7'-3\""],
  ["2×8", "SPF", "#2", "12'-10\"", "11'-8\"", "9'-7\""],
  ["2×10", "SPF", "#2", "16'-5\"", "14'-11\"", "12'-3\""],
  ["2×12", "SPF", "#2", "19'-11\"", "18'-1\"", "14'-11\""],
];

// ── Steel Beam Span (W-shapes, uniformly loaded, simple span) ──
export const steelBeamHeaders = ["Shape", "Weight (lb/ft)", "Depth (in)", "Ix (in⁴)", "Sx (in³)", "Max Span (Fb=24ksi, 1k/ft)"];
export const steelBeamRows: (string | number)[][] = [
  ["W6×9", 9, 5.9, 16.4, 5.56, "11'"],
  ["W8×10", 10, 7.89, 30.8, 7.81, "15'"],
  ["W8×15", 15, 8.11, 48.0, 11.8, "19'"],
  ["W10×12", 12, 9.87, 53.8, 10.9, "17'"],
  ["W10×22", 22, 10.17, 118, 23.2, "28'"],
  ["W12×16", 16, 11.99, 103, 17.1, "24'"],
  ["W12×26", 26, 12.22, 204, 33.4, "34'"],
  ["W14×22", 22, 13.74, 199, 29.0, "32'"],
  ["W14×30", 30, 13.84, 291, 42.0, "38'"],
  ["W16×26", 26, 15.69, 301, 38.4, "36'"],
  ["W16×36", 36, 15.86, 448, 56.5, "42'"],
  ["W18×35", 35, 17.7, 510, 57.6, "44'"],
  ["W21×44", 44, 20.66, 843, 81.6, "52'"],
  ["W24×55", 55, 23.57, 1350, 114, "62'"],
];

// ── LVL/Glulam Header Spans (2-ply, carrying load from one floor) ──
export const headerSpanHeaders = ["Header Size", "Supported Length ≤6'", "≤8'", "≤10'", "≤12'"];
export const headerSpanRows: (string | number)[][] = [
  ["(2) 2×6", "4'-0\"", "3'-6\"", "3'-0\"", "2'-6\""],
  ["(2) 2×8", "5'-6\"", "4'-6\"", "4'-0\"", "3'-6\""],
  ["(2) 2×10", "7'-0\"", "6'-0\"", "5'-0\"", "4'-6\""],
  ["(2) 2×12", "8'-6\"", "7'-0\"", "6'-0\"", "5'-6\""],
  ["3-1/2×9-1/4 LVL", "8'-0\"", "7'-0\"", "6'-0\"", "5'-6\""],
  ["3-1/2×11-7/8 LVL", "10'-6\"", "9'-0\"", "8'-0\"", "7'-0\""],
  ["3-1/2×14 LVL", "12'-6\"", "11'-0\"", "9'-6\"", "8'-6\""],
  ["5-1/4×9-1/4 Glulam", "10'-0\"", "8'-6\"", "7'-6\"", "7'-0\""],
  ["5-1/4×11-7/8 Glulam", "13'-0\"", "11'-6\"", "10'-0\"", "9'-0\""],
];

// ── Combined Anchor Data (Expansion + Wedge) ──
export const anchorHeaders = ["Bolt Dia.", "Type", "Embed. Depth", "Min. Edge / Hole", "Tension (lb)", "Shear (lb)", "Torque (ft-lb)"];
export const anchorRows: (string | number)[][] = [
  // Expansion anchors in 3000 PSI normal-weight concrete
  ["1/4\"",  "Expansion", "1-1/8\"",  "1-1/4\"",  530,   640,   4],
  ["3/8\"",  "Expansion", "1-5/8\"",  "1-7/8\"",  1200,  1480,  10],
  ["1/2\"",  "Expansion", "2-1/4\"",  "2-1/4\"",  2150,  2680,  25],
  ["5/8\"",  "Expansion", "2-3/4\"",  "3-1/8\"",  3480,  4200,  50],
  ["3/4\"",  "Expansion", "3-1/4\"",  "3-3/4\"",  5090,  6130,  75],
  ["7/8\"",  "Expansion", "3-7/8\"",  "4-3/8\"",  6930,  6880,  110],
  ["1\"",    "Expansion", "4-1/2\"",  "5\"",       8700,  8720,  150],
  // Wedge anchors
  ["1/4\"",  "Wedge",     "1\"",      "1/4\"",     675,   810,   "—"],
  ["3/8\"",  "Wedge",     "1-1/2\"",  "3/8\"",     1590,  1900,  "—"],
  ["1/2\"",  "Wedge",     "2-1/4\"",  "1/2\"",     3060,  3670,  "—"],
  ["5/8\"",  "Wedge",     "2-3/4\"",  "5/8\"",     4910,  5490,  "—"],
  ["3/4\"",  "Wedge",     "3-1/4\"",  "3/4\"",     7090,  7740,  "—"],
  ["7/8\"",  "Wedge",     "3-7/8\"",  "7/8\"",     9480,  9960,  "—"],
  ["1\"",    "Wedge",     "4-1/2\"",  "1\"",       12350, 13200, "—"],
];

// ── Seismic Bracing Intervals (SMACNA / IBC) ──
export const seismicBraceHeaders = ["System", "Longitudinal", "Lateral", "First Brace"];
export const seismicBraceRows: (string | number)[][] = [
  ["HVAC Duct (≤6 ft²)", "40 ft", "40 ft", "≤12 ft from end"],
  ["HVAC Duct (>6 ft²)", "30 ft", "30 ft", "≤12 ft from end"],
  ["Pipe (≤2½\")", "40 ft", "40 ft", "≤12 ft"],
  ["Pipe (>2½\")", "40 ft", "40 ft", "≤12 ft"],
  ["Conduit/Cable Tray", "40 ft", "40 ft", "≤12 ft"],
  ["Sprinkler Main", "40 ft", "40 ft", "≤12 ft"],
  ["Sprinkler Branch (≤2½\")", "Per NFPA 13", "Per NFPA 13", "—"],
  ["Equipment (>400 lb)", "Anchorage Required", "Anchorage Required", "All supports"],
  ["Equipment (>20 lb, Ip=1.5)", "Anchorage Required", "Anchorage Required", "All supports"],
];

// ── Combined Design Loads (Dead + Live) ──
export const designLoadHeaders = ["Material / Use", "Load Type", "Weight (PSF)", "Concentrated (lb)"];
export const designLoadRows: (string | number)[][] = [
  ["Concrete (per inch)",            "Dead", 12.5,  "—"],
  ["Lightweight Concrete (per inch)","Dead", 8.3,   "—"],
  ["Steel Deck (20 ga)",             "Dead", 2.5,   "—"],
  ["Steel Deck (18 ga)",             "Dead", 3.5,   "—"],
  ["Plywood Subfloor (3/4\")",       "Dead", 2.3,   "—"],
  ["Hardwood Flooring",              "Dead", 4.0,   "—"],
  ["Ceramic Tile on Mortar",         "Dead", 16.0,  "—"],
  ["Carpet & Pad",                   "Dead", 1.5,   "—"],
  ["Gypsum Board (5/8\")",           "Dead", 2.8,   "—"],
  ["Gypsum Board (1/2\")",           "Dead", 2.2,   "—"],
  ["Acoustical Ceiling",             "Dead", 1.5,   "—"],
  ["Suspended Ceiling Grid",         "Dead", 1.0,   "—"],
  ["Insulation (batt, per inch)",    "Dead", 0.3,   "—"],
  ["Rigid Insulation (per inch)",    "Dead", 1.5,   "—"],
  ["Metal Stud Wall (20ga + 2-layer GWB)", "Dead", 10.0, "—"],
  ["Brick Veneer (4\")",             "Dead", 40.0,  "—"],
  ["CMU (8\" hollow)",               "Dead", 33.0,  "—"],
  ["CMU (8\" grouted)",              "Dead", 80.0,  "—"],
  ["Built-up Roof (4-ply)",          "Dead", 5.5,   "—"],
  ["Standing Seam Metal Roof",       "Dead", 1.5,   "—"],
  ["Membrane Roof",                  "Dead", 1.0,   "—"],
  ["Wood Framed Floor (2×10 @16\" OC)", "Dead", 6.0, "—"],
  ["Steel Joist Floor (typ.)",       "Dead", 8.0,   "—"],
  ["Residential (private)",          "Live", 40,    "—"],
  ["Residential (public areas)",     "Live", 100,   "—"],
  ["Office",                         "Live", 50,    2000],
  ["Retail (first floor)",           "Live", 100,   1000],
  ["Retail (upper floors)",          "Live", 75,    1000],
  ["Assembly (fixed seats)",         "Live", 60,    "—"],
  ["Assembly (movable seats)",       "Live", 100,   "—"],
  ["Corridors (above first floor)",  "Live", 80,    "—"],
  ["Corridors (first floor)",        "Live", 100,   "—"],
  ["Light Storage",                  "Live", 125,   "—"],
  ["Heavy Storage",                  "Live", 250,   "—"],
  ["Mechanical Equipment Rooms",     "Live", 150,   "—"],
  ["Parking Garage",                 "Live", 40,    3000],
  ["Hospital Patient Rooms",         "Live", 40,    1000],
  ["Hospital Corridors",             "Live", 80,    1000],
  ["School Classrooms",              "Live", 40,    1000],
  ["School Corridors",               "Live", 80,    1000],
  ["Library Reading Rooms",          "Live", 60,    1000],
  ["Library Stack Rooms",            "Live", 150,   1000],
  ["Roof (ordinary)",                "Live", 20,    "—"],
  ["Roof (landscaped)",              "Live", 100,   "—"],
  ["Balconies (exterior)",           "Live", 100,   "—"],
  ["Decks",                          "Live", 40,    "—"],
  ["Stairs & Exit Ways",             "Live", 100,   "—"],
];

// ── Seismic Design Category reference ──
export const SEISMIC_CATEGORIES = [
  { sdc: "A", description: "Very low seismicity", ip: 1.0, sds_range: "SDS < 0.167" },
  { sdc: "B", description: "Low seismicity", ip: 1.0, sds_range: "0.167 ≤ SDS < 0.33" },
  { sdc: "C", description: "Moderate seismicity", ip: 1.0, sds_range: "0.33 ≤ SDS < 0.50" },
  { sdc: "D", description: "High seismicity", ip: 1.5, sds_range: "0.50 ≤ SDS < 0.75" },
  { sdc: "E", description: "Very high seismicity", ip: 1.5, sds_range: "0.75 ≤ SDS < 1.0" },
  { sdc: "F", description: "Near major fault", ip: 1.5, sds_range: "SDS ≥ 1.0" },
];
